import { toDataURL } from "./Client";
import { decrypt, encrypt } from "./Crypto";

const SUFFIX = "$confidential";
const SUFFIX_RE = /\$confidential$/;

// Types that can appear (almost) anywhere in the structure
type BaseItem<O> =
  | O
  | BaseItem<O>[]
  | string
  | number
  | boolean
  | undefined
  | null;
type ExtendedItem<O> = BaseItem<O> | File;
// Definition how the root metadata object must look
type Metadata<C, O> = O & {
  confidential: C;
  confidential$confidential?: never;
};
// Variant of the root metadata object that doesn't require confidential to exist
type MetadataOpt<C, O> = O & {
  confidential?: C;
  confidential$confidential?: never;
};

// Aliases
export type BuilderItem = ExtendedItem<BuilderObject>;
export type StoredItem = BaseItem<StoredObject>;
export type PublicItem = BaseItem<PublicObject>;
export type ConfidentialItem = MergedItem;
export type MergedItem = BaseItem<MergedObject>;

// Definition on how objects must look
export type BuilderObject = {
  [key: string | number]: BuilderItem;
};
export type StoredObject = {
  [key: `${string}$confidential`]: CInner;
  [key: string | number]: StoredItem;
};
export type PublicObject = {
  // null indicates that a confidential value exists
  [key: `${string}$confidential`]: null;
  [key: string | number]: PublicItem;
};
export type ConfidentialObject = {
  // Indicates that some (or all) fields have been set this way. Currently
  // unused but it is still set by getPublicInner.
  $confidential?: null;
  // This includes keys ending with $confidential
  [key: string | number]: MergedItem;
};
export type MergedObject = ConfidentialObject;

// Basic summary of public information
type CSummary = {
  key_id: string;
};
// Root "confidential" in storage
export type CStored =
  | {
      key_id: string;
      kdf: number;
      data: string;
    }
  | {
      key_id: string;
      kdf: number;
      url: string; // TODO: Not yet supported
    };
// As used in "$confidential" in storage
type CInner =
  | number
  | {
      // TODO: kdf+url not yet supported
      kdf: number;
      url: string;
    };

export type BuilderMetadata = MetadataOpt<never, BuilderObject>;
export type StoredMetadata = Metadata<CStored, StoredObject>;
export type PublicMetadata = Metadata<CSummary, PublicObject>;
export type ConfidentialMetadata = Metadata<CSummary, MergedObject>;
export type MergedMetadata = ConfidentialMetadata;
export type ProcessedMetadata =
  | BuilderMetadata
  | PublicMetadata
  | ConfidentialMetadata
  | MergedMetadata;

export function getPublic(obj: StoredMetadata): PublicMetadata {
  const map_obj = (v: StoredObject): PublicObject => {
    const out: PublicObject = {};
    for (const key in v) {
      out[key] = key.endsWith(SUFFIX) ? null : map_item(v[key]);
    }
    return out;
  };
  const map_item = (v: StoredItem): PublicItem => {
    if (Array.isArray(v)) {
      return v.map(map_item);
    } else if (v && typeof v === "object") {
      return map_obj(v);
    } else {
      return v;
    }
  };
  return {
    confidential: { key_id: obj.confidential.key_id },
    ...map_obj(obj),
  };
}
export async function getConfidential(
  obj: StoredMetadata,
  rootKey: CryptoKey,
  overwrite: Boolean,
): Promise<ConfidentialMetadata> {
  // Force decrypted to be an array
  const decrypted: StoredItem = await decrypt(obj.confidential, rootKey);
  if (!Array.isArray(decrypted)) {
    throw "Not a valid cNFT";
  }

  // NOTE: We're using undefined to indicate non-presense of confidential data.
  const map_obj = async (
    v: StoredObject,
    inConfidential: Boolean,
  ): Promise<ConfidentialObject> => {
    const out: ConfidentialObject = {};
    let has_confidential = false;
    for (const key in v) {
      let value;
      if (key.endsWith(SUFFIX)) {
        value = await processsConfidential(
          v[key] as CInner,
          decrypted,
          rootKey,
        );
      } else if (inConfidential) {
        value = v[key];
      }
      // Don't include fields if we're not in a confidential object.
      if (value !== undefined) {
        has_confidential = true;
        const out_value = await map_item(value, true);
        // Don't include fields without confidential data (marked as undefined)
        if (out_value !== undefined) {
          out[key] = out_value;
        }
      }
    }
    return has_confidential ? out : {};
  };
  const map_item = async (
    v: StoredItem,
    inConfidential: Boolean,
  ): Promise<ConfidentialItem> => {
    if (Array.isArray(v)) {
      const out: ConfidentialItem[] = [];
      let has_confidential = false;
      for (const i in v) {
        // Sets items without confidential data to undefined
        out[i] = await map_item(v[i], inConfidential);
        if (out[i] !== undefined) has_confidential = true;
      }
      return has_confidential ? out : undefined;
    } else if (v && typeof v === "object") {
      return await map_obj(v, inConfidential);
    } else {
      return inConfidential ? v : undefined;
    }
  };

  return {
    confidential: { key_id: obj.confidential.key_id },
    ...(await map_obj(obj, false)),
  };
}
export async function getMerged(
  obj: StoredMetadata,
  rootKey: CryptoKey,
  overwrite: Boolean,
  forceSuffix: Boolean,
): Promise<MergedMetadata> {
  // Force decrypted to be an array
  const decrypted: StoredItem = await decrypt(obj.confidential, rootKey);
  if (!Array.isArray(decrypted)) {
    throw "Not a valid cNFT";
  }

  const map_obj = async (v: StoredObject): Promise<ConfidentialObject> => {
    const out: ConfidentialObject = {};
    const insert = (key: string, value: ConfidentialItem) => {
      const key_no_suffix = key.replace(SUFFIX_RE, "");
      const write_key =
        forceSuffix || (!overwrite && key_no_suffix in out)
          ? key
          : key_no_suffix;
      out[write_key] = value;
    };

    // First add the public data (otherwise overwrite wouldn't work properly)
    for (const key in v) {
      if (key.endsWith(SUFFIX)) continue;
      out[key] = await map_item(v[key]);
    }
    // Then add the named confidential data
    for (const key in v) {
      if (!key.endsWith(SUFFIX) || key === SUFFIX) continue;
      const value = await processsConfidential(
        v[key] as CInner,
        decrypted,
        rootKey,
      );
      insert(key, await map_item(value));
    }
    // Finally process unnamed $confidential
    if ("$confidential" in v) {
      const value = await processsConfidential(
        v.$confidential as CInner,
        decrypted,
        rootKey,
      );
      if (typeof value === "object")
        for (const k in value) {
          const key = k.endsWith(SUFFIX) ? k : k + SUFFIX;
          insert(key, await map_item(value));
        }
    }
    return out;
  };
  const map_item = async (v: StoredItem): Promise<MergedItem> => {
    if (Array.isArray(v)) {
      const out: MergedItem[] = [];
      for (const i in v) {
        out[i] = await map_item(v[i]);
      }
      return out;
    } else if (v && typeof v === "object") {
      return await map_obj(v);
    } else {
      return v;
    }
  };

  return {
    confidential: { key_id: obj.confidential.key_id },
    ...(await map_obj(obj)),
  };
}

async function processsConfidential(
  conf: CInner,
  decrypted: StoredItem[],
  rootKey: CryptoKey,
): Promise<StoredItem> {
  if (typeof conf === "number") {
    return decrypted[conf];
  }
  return decrypt(conf, rootKey);
}

// TODO: We should probably sort the json
export async function buildAndEncrypt(
  obj: BuilderObject,
  key_id: string,
  rootKey: CryptoKey,
  kdf_start?: number,
): Promise<StoredMetadata> {
  kdf_start ??= 0;
  // Reserve first kdf for encrypting cdata.
  const cdata_kdf = kdf_start;
  kdf_start += 1;
  const cdata: StoredItem[] = [];

  const map_obj = async (v: BuilderObject): Promise<StoredObject> => {
    const out: StoredObject = {};
    for (const key in v) {
      // NOTE: This also allows specifying the unnamed `$confidential`.
      if (key.endsWith(SUFFIX)) {
        const index: CInner = cdata.length;
        cdata.push(await map_item(v[key]));
        out[key] = index;
      } else {
        out[key] = await map_item(v[key]);
      }
    }
    return out;
  };
  const map_item = async (v: BuilderItem): Promise<StoredItem> => {
    if (v instanceof File) {
      // TODO: Allow this to be uploaded separately instead of forcing data
      // urls. Maybe decide based on file size. It might also be desirable to
      // take name and type from the file, but we don't always want that.
      return await toDataURL(v);
    } else if (Array.isArray(v)) {
      const out: StoredItem[] = [];
      for (const i in v) {
        out[i] = await map_item(v[i]);
      }
      return out;
    } else if (v && typeof v === "object") {
      return await map_obj(v);
    } else {
      return v;
    }
  };

  const out_pub = await map_obj(obj);
  return {
    confidential: {
      // TODO: We may want to change the key_id into a format (e.g. URL) that
      // contains the ID and where the server is (for now that is hard-coded).
      key_id,
      ...(await encrypt(cdata, rootKey, cdata_kdf)),
    },
    ...out_pub,
  };
}
